import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from 'constants/apiConfig';
import { parseError } from 'hooks/shared/error';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectGroupGuid } from 'slices/commonSelectors';
import { keys as rosterKeys } from 'hooks/incident/useRoster';
import type { RosterTemplateNodeDto } from 'components/IncidentConfig/roster/templates/rosterTemplateTypes';
import { useUscgFocRequirementsFlag } from 'utils/featureFlags';
import { getUscgFocRosterTemplates } from 'components/MapPage/CorporateViewTabs/PlanLibraryTab/uscgFocPlanning';

export type RosterTemplateLibraryEntry = {
  id: string;
  groupGuid: string;
  name: string;
  description: string | null;
  attachOnly: boolean;
  nodes: RosterTemplateNodeDto[];
  archived: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
};

export type RosterTemplateWritePayload = {
  name: string;
  description?: string | null;
  attachOnly?: boolean;
  nodes?: RosterTemplateNodeDto[];
};

export type SaveRosterTemplateFromIncidentPayload = {
  incidentId: string;
  name: string;
  description?: string | null;
  /** Capture only this seat's subtree, saved as an attach-only expansion. */
  rootPositionId?: string | null;
  /** Save the captured tree as an expansion even when capturing the whole chart. */
  attachOnly?: boolean;
};

function templateError(error: unknown): string {
  const parsed = parseError(error);
  if (parsed) return parsed;
  const data = (error as { response?: { data?: { error?: unknown } } })
    ?.response?.data?.error;
  if (typeof data === 'string' && data.trim()) return data;
  return 'Something went wrong';
}

export const rosterTemplateLibraryKeys = {
  all: ['roster-template-library'] as const,
  list: (groupGuid: string) =>
    [...rosterTemplateLibraryKeys.all, 'list', groupGuid] as const,
};

/**
 * Invalidate both the library list and the roster Templates sidebar catalog,
 * since the sidebar serves org templates merged into the built-in catalog and
 * would otherwise show a stale set.
 */
function useInvalidateRosterTemplates() {
  const groupGuid = useSelector(selectGroupGuid);
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: rosterTemplateLibraryKeys.list(groupGuid ?? ''),
    });
    void queryClient.invalidateQueries({
      queryKey: [rosterKeys.rosterTemplates],
    });
  };
}

export function useRosterTemplateLibrary(options?: { enabled?: boolean }) {
  const groupGuid = useSelector(selectGroupGuid);
  const uscgFocEnabled = useUscgFocRequirementsFlag();
  return useQuery({
    queryKey: [
      ...rosterTemplateLibraryKeys.list(groupGuid ?? ''),
      uscgFocEnabled ? 'uscg-foc' : '',
    ],
    enabled: Boolean(groupGuid) && options?.enabled !== false,
    queryFn: async () => {
      const response = await axios.get<RosterTemplateLibraryEntry[]>(
        config.apiGateway.rosterTemplateLibrary,
        { params: { groupGuid } }
      );
      const templates = response.data ?? [];
      if (!uscgFocEnabled || !groupGuid) return templates;
      const fixtures = getUscgFocRosterTemplates(groupGuid);
      const seen = new Set(templates.map((template) => template.id));
      return [
        ...templates,
        ...fixtures.filter((template) => !seen.has(template.id)),
      ];
    },
  });
}

export function useCreateRosterTemplate() {
  const groupGuid = useSelector(selectGroupGuid);
  const invalidate = useInvalidateRosterTemplates();
  return useMutation({
    mutationFn: async (payload: RosterTemplateWritePayload) => {
      const response = await axios.post<RosterTemplateLibraryEntry>(
        config.apiGateway.rosterTemplateLibrary,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Template created');
    },
    onError: (error) => toast.error(templateError(error)),
  });
}

export function useUpdateRosterTemplate() {
  const groupGuid = useSelector(selectGroupGuid);
  const invalidate = useInvalidateRosterTemplates();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: RosterTemplateWritePayload & { id: string }) => {
      const response = await axios.put<RosterTemplateLibraryEntry>(
        `${config.apiGateway.rosterTemplateLibrary}/${id}`,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: invalidate,
    onError: (error) => toast.error(templateError(error)),
  });
}

export function useArchiveRosterTemplate() {
  const groupGuid = useSelector(selectGroupGuid);
  const invalidate = useInvalidateRosterTemplates();
  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${config.apiGateway.rosterTemplateLibrary}/${id}`, {
        params: { groupGuid },
      });
      return id;
    },
    onSuccess: () => {
      invalidate();
      toast.success('Template deleted');
    },
    onError: (error) => toast.error(templateError(error)),
  });
}

export function useSaveRosterTemplateFromIncident() {
  const groupGuid = useSelector(selectGroupGuid);
  const invalidate = useInvalidateRosterTemplates();
  return useMutation({
    mutationFn: async (payload: SaveRosterTemplateFromIncidentPayload) => {
      const response = await axios.post<RosterTemplateLibraryEntry>(
        `${config.apiGateway.rosterTemplateLibrary}/from-incident`,
        { ...payload, groupGuid }
      );
      return response.data;
    },
    onSuccess: (template) => {
      invalidate();
      toast.success(
        `Saved “${template.name}” with ${template.nodes.length} seat${
          template.nodes.length === 1 ? '' : 's'
        }`
      );
    },
    onError: (error) => toast.error(templateError(error)),
  });
}
